import {EntityListFilter} from 'components';
import {useEffect, useMemo, useState} from 'react';
import {RegulatoryDocumentStatus, VexWorkbenchItemSimplified} from 'types';
import {useUserContext} from 'authentication/UserContext';

export const useVexWorkbenchItemsFilter = (
	items: VexWorkbenchItemSimplified[],
) => {
	const [filteredItems, setFilteredItems] = useState<
		VexWorkbenchItemSimplified[]
	>([]);
	const [filters, setFilters] = useState<EntityListFilter[]>([]);
	const {myVexClusters} = useUserContext();

	useEffect(() => {
		const marketFilters = filters
			.filter(f => f.columnKey === 'LegalArea')
			.map(f => f.selection);
		let newItems = items;
		if (marketFilters.length > 0) {
			newItems = items.filter(i =>
				i.marketNames?.some(m => marketFilters.indexOf(m) !== -1),
			);
		}

		const statusFilters = filters
			.filter(f => f.columnKey === 'Status')
			.map(f => f.selection);

		if (statusFilters.length > 0) {
			newItems = newItems.filter(
				i => statusFilters.indexOf(i.regulatoryDocumentStatus) !== -1,
			);
		}

		const vexClusterFilters = filters
			.filter(f => f.columnKey === 'VexCluster')
			.map(f => f.selection);

		if (vexClusterFilters.length > 0) {
			newItems = newItems.filter(i =>
				i.vexClusterData?.some(
					vcd =>
						vexClusterFilters.indexOf(vcd.vexClusterName) !== -1 &&
						vcd.paragraphCount > 0,
				),
			);
		}
		setFilteredItems(newItems);
	}, [items, filters]);

	const filterLegalAreas = useMemo(() => {
		const markets = items.flatMap(
			i =>
				i.marketNames.map(m => {
					return {name: m, optionKey: m};
				}) || [],
		);
		return markets.filter(
			(market, index, self) =>
				self.findIndex(t => t.name === market.name) === index,
		);
	}, [items]);

	const filterStatuses = useMemo(() => {
		const statuses = items
			.map(i => i.regulatoryDocumentStatus as RegulatoryDocumentStatus)
			.map(s => {
				return {name: s, optionKey: s};
			});
		return statuses.filter(
			(status, index, self) =>
				self.findIndex(t => t.name === status.name) === index,
		);
	}, [items]);

	const filterVexClusters = useMemo(() => {
		const clusters = items
			.flatMap(i => i.vexClusterData.map(vcd => vcd.vexClusterName))
			.map(vcaName => {
				return {name: vcaName, optionKey: vcaName};
			});
		return clusters.filter(
			(cluster, index, self) =>
				self.findIndex(c => c.name === cluster.name) === index,
		);
	}, [items, myVexClusters]);

	return {
		filteredItems,
		filters,
		setFilters,
		filterLegalAreas,
		filterStatuses,
		filterVexClusters,
	};
};
